import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import Modal from '../components/Common/Modal';

const ProfilePage = () => {
  const { isAuthenticated, user, logout, updateUserDetails } = useAuth();
  const navigate = useNavigate();

  // Form states
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    profilePicture: null
  });
  
  // Editing states
  const [isEditingName, setIsEditingName] = useState(false);
  const [isEditingEmail, setIsEditingEmail] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);

  // Modal state
  const [modal, setModal] = useState({
    isOpen: false,
    type: 'info',
    title: '',
    message: '',
    onConfirm: null
  });

  const showModal = (type, title, message, onConfirm = null) => {
    setModal({
      isOpen: true,
      type,
      title,
      message,
      onConfirm
    });
  };

  const closeModal = () => {
    setModal(prev => ({ ...prev, isOpen: false }));
  };

  // Update form when user data changes
  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || '',
        email: user.email || '',
        profilePicture: user.profilePicture || null
      });
    }
  }, [user]);

  if (!isAuthenticated) {
    return (
      <div className="profile-page">
        <div className="container">
          <div className="auth-required">
            <h2>Login Required</h2>
            <p>Please login to view your profile</p>
            <button onClick={() => navigate('/auth')} className="btn btn-primary">
              Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSaveName = async () => {
    if (!formData.name.trim()) {
      showModal('error', 'Error', 'Please enter a valid name');
      return;
    }

    try {
      setIsUpdating(true);
      const result = await updateUserDetails({ name: formData.name.trim() });
      
      if (result.success) {
        setIsEditingName(false);
        showModal('success', 'Success', 'Name updated successfully!');
      } else {
        showModal('error', 'Error', 'Failed to update name: ' + result.error);
      }
    } catch (error) {
      showModal('error', 'Error', 'Failed to update name');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleSaveEmail = async () => {
    if (formData.email && !isValidEmail(formData.email)) {
      showModal('error', 'Error', 'Please enter a valid email address');
      return;
    }

    try {
      setIsUpdating(true);
      const result = await updateUserDetails({ email: formData.email.trim() });
      
      if (result.success) {
        setIsEditingEmail(false);
        showModal('success', 'Success', 'Email updated successfully!');
      } else {
        showModal('error', 'Error', 'Failed to update email: ' + result.error);
      }
    } catch (error) {
      showModal('error', 'Error', 'Failed to update email');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleProfilePictureChange = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      showModal('error', 'Error', 'Please select an image file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) { // 5MB limit
      showModal('error', 'Error', 'Image size should be less than 5MB');
      return;
    }

    const reader = new FileReader();
    reader.onload = async (e) => {
      const base64 = e.target.result;
      
      try {
        setIsUpdating(true);
        const result = await updateUserDetails({ profilePicture: base64 });
        
        if (result.success) {
          setFormData(prev => ({ ...prev, profilePicture: base64 }));
          showModal('success', 'Success', 'Profile picture updated successfully!');
        } else {
          showModal('error', 'Error', 'Failed to update profile picture: ' + result.error);
        }
      } catch (error) {
        showModal('error', 'Error', 'Failed to update profile picture');
      } finally {
        setIsUpdating(false);
      }
    };
    
    reader.readAsDataURL(file);
  };

  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleLogout = async () => {
    showModal(
      'confirm',
      'Confirm Logout',
      'Are you sure you want to logout?',
      async () => {
        try {
          await logout();
          navigate('/');
        } catch (error) {
          console.error('Logout error:', error);
          showModal('error', 'Error', 'Failed to logout properly. Please try again.');
        }
      }
    );
  };

  const handleCancelEdit = (field) => {
    if (field === 'name') {
      setFormData(prev => ({ ...prev, name: user?.name || '' }));
      setIsEditingName(false);
    } else if (field === 'email') {
      setFormData(prev => ({ ...prev, email: user?.email || '' }));
      setIsEditingEmail(false);
    }
  };

  return (
    <div className="profile-page">
      <div className="container">
        <div className="profile-content">
          <h1>My Profile</h1>
          
          {/* Profile Picture Section */}
          <div className="profile-picture-section">
            <div className="profile-picture-container">
              {formData.profilePicture ? (
                <img 
                  src={formData.profilePicture.startsWith('data:') 
                    ? formData.profilePicture 
                    : `data:image/jpeg;base64,${formData.profilePicture}`
                  } 
                  alt="Profile" 
                  className="profile-picture"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
              ) : null}
              <div 
                className="default-avatar" 
                style={{display: formData.profilePicture ? 'none' : 'flex'}}
              >
                👤
              </div>
              <label className="camera-button" htmlFor="profile-upload">
                📷
                <input
                  id="profile-upload"
                  type="file"
                  accept="image/*"
                  onChange={handleProfilePictureChange}
                  style={{ display: 'none' }}
                />
              </label>
            </div>
            <p className="upload-hint">Click the camera icon to change your profile picture</p>
          </div>

          {/* User Information */}
          <div className="user-info-section">
            {/* Mobile Number (Read-only) */}
            <div className="info-card">
              <div className="info-header">
                <span className="info-icon">📱</span>
                <h3>Mobile Number</h3>
              </div>
              <div className="info-content">
                <span className="info-value">{user?.mobile || 'Not provided'}</span>
                <p className="info-hint">Mobile number cannot be changed</p>
              </div>
            </div>

            {/* Full Name */}
            <div className="info-card">
              <div className="info-header">
                <span className="info-icon">👤</span>
                <h3>Full Name</h3>
                <button
                  className="edit-button"
                  onClick={() => {
                    if (isEditingName) {
                      handleSaveName();
                    } else {
                      setIsEditingName(true);
                    }
                  }}
                  disabled={isUpdating}
                >
                  {isUpdating && isEditingName ? '⏳' : isEditingName ? '✓' : '✏️'}
                </button>
              </div>
              <div className="info-content">
                {isEditingName ? (
                  <div className="edit-field">
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => handleInputChange('name', e.target.value)}
                      placeholder="Enter your full name"
                      className="edit-input"
                    />
                    <button 
                      className="cancel-button"
                      onClick={() => handleCancelEdit('name')}
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <span className="info-value">{formData.name || 'Not provided'}</span>
                )}
              </div>
            </div>

            {/* Email */}
            <div className="info-card">
              <div className="info-header">
                <span className="info-icon">📧</span>
                <h3>Email Address</h3>
                <button
                  className="edit-button"
                  onClick={() => {
                    if (isEditingEmail) {
                      handleSaveEmail();
                    } else {
                      setIsEditingEmail(true);
                    }
                  }}
                  disabled={isUpdating}
                >
                  {isUpdating && isEditingEmail ? '⏳' : isEditingEmail ? '✓' : '✏️'}
                </button>
              </div>
              <div className="info-content">
                {isEditingEmail ? (
                  <div className="edit-field">
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      placeholder="Enter your email (optional)"
                      className="edit-input"
                    />
                    <button 
                      className="cancel-button"
                      onClick={() => handleCancelEdit('email')}
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <span className="info-value">{formData.email || 'Not provided'}</span>
                )}
                <p className="info-hint">Optional - for order confirmations and updates</p>
              </div>
            </div>

            {/* Account Stats */}
            <div className="stats-card">
              <h3>Account Statistics</h3>
              <div className="stats-grid">
                <div className="stat-item">
                  <span className="stat-value">{user?.rewardPoints || 0}</span>
                  <span className="stat-label">Reward Points</span>
                </div>
                <div className="stat-divider"></div>
                <div className="stat-item">
                  <span className="stat-value">{user?.ordersCount || 0}</span>
                  <span className="stat-label">Orders Placed</span>
                </div>
              </div>
            </div>
          </div>

          {/* Logout Button */}
          <div className="logout-section">
            <button 
              onClick={handleLogout} 
              className="logout-button"
              disabled={isUpdating}
            >
              🚪 Logout
            </button>
          </div>
        </div>
      </div>

      <Modal
        isOpen={modal.isOpen}
        type={modal.type}
        title={modal.title}
        message={modal.message}
        onConfirm={modal.onConfirm}
        onClose={closeModal}
      />

      <style jsx>{`
        .profile-page {
          min-height: 100vh;
          background-color: #fff8f0;
          padding: 40px 0 80px;
        }
        
        .auth-required {
          text-align: center;
          padding: 100px 20px;
        }
        
        .auth-required h2 {
          color: #C2185B;
          margin-bottom: 15px;
        }
        
        .profile-content {
          max-width: 600px;
          margin: 0 auto;
        }
        
        .profile-content h1 {
          color: #C2185B;
          text-align: center;
          margin-bottom: 40px;
          font-size: 2.5rem;
        }
        
        .profile-picture-section {
          text-align: center;
          margin-bottom: 40px;
          background: white;
          padding: 30px;
          border-radius: 20px;
          box-shadow: 0 5px 20px rgba(0, 0, 0, 0.1);
        }
        
        .profile-picture-container {
          position: relative;
          display: inline-block;
          margin-bottom: 15px;
        }
        
        .profile-picture {
          width: 120px;
          height: 120px;
          border-radius: 50%;
          object-fit: cover;
          border: 4px solid #C2185B;
        }
        
        .default-avatar {
          width: 120px;
          height: 120px;
          border-radius: 50%;
          background: linear-gradient(45deg, #C2185B, #a855f7);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 60px;
          border: 4px solid #C2185B;
        }
        
        .camera-button {
          position: absolute;
          bottom: 0;
          right: 0;
          width: 36px;
          height: 36px;
          border-radius: 50%;
          background: #C2185B;
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          border: 3px solid white;
          font-size: 18px;
          transition: all 0.3s ease;
        }
        
        .camera-button:hover {
          background: #AD1457;
          transform: scale(1.1);
        }
        
        .upload-hint {
          color: #666;
          font-size: 14px;
        }
        
        .user-info-section {
          margin-bottom: 30px;
        }
        
        .info-card {
          background: white;
          border-radius: 15px;
          padding: 25px;
          margin-bottom: 20px;
          box-shadow: 0 5px 20px rgba(0, 0, 0, 0.1);
        }
        
        .info-header {
          display: flex;
          align-items: center;
          gap: 10px;
          margin-bottom: 15px;
        }
        
        .info-icon {
          font-size: 20px;
        }
        
        .info-header h3 {
          color: #C2185B;
          margin: 0;
          flex: 1;
        }
        
        .edit-button {
          background: none;
          border: 2px solid #C2185B;
          color: #C2185B;
          padding: 8px 12px;
          border-radius: 8px;
          cursor: pointer;
          font-size: 16px;
          transition: all 0.3s ease;
        }
        
        .edit-button:hover:not(:disabled) {
          background: #C2185B;
          color: white;
        }
        
        .edit-button:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
        
        .info-content {
          margin-left: 30px;
        }
        
        .info-value {
          font-size: 18px;
          color: #333;
          font-weight: 500;
        }
        
        .info-hint {
          color: #666;
          font-size: 14px;
          margin-top: 5px;
          margin-bottom: 0;
        }
        
        .edit-field {
          display: flex;
          gap: 10px;
          align-items: center;
          flex-wrap: wrap;
        }
        
        .edit-input {
          flex: 1;
          min-width: 200px;
          padding: 12px 15px;
          border: 2px solid #C2185B;
          border-radius: 8px;
          font-size: 16px;
          outline: none;
        }
        
        .cancel-button {
          background: #ef4444;
          color: white;
          border: none;
          padding: 8px 16px;
          border-radius: 8px;
          cursor: pointer;
          font-size: 14px;
          transition: all 0.3s ease;
        }
        
        .cancel-button:hover {
          background: #dc2626;
        }
        
        .stats-card {
          background: white;
          border-radius: 15px;
          padding: 25px;
          box-shadow: 0 5px 20px rgba(0, 0, 0, 0.1);
        }
        
        .stats-card h3 {
          color: #C2185B;
          margin-bottom: 20px;
          text-align: center;
        }
        
        .stats-grid {
          display: flex;
          align-items: center;
          justify-content: space-around;
        }
        
        .stat-item {
          text-align: center;
        }
        
        .stat-value {
          display: block;
          font-size: 2rem;
          font-weight: bold;
          color: #C2185B;
          margin-bottom: 5px;
        }
        
        .stat-label {
          font-size: 14px;
          color: #666;
        }
        
        .stat-divider {
          width: 1px;
          height: 40px;
          background: #e5e5e5;
        }
        
        .logout-section {
          text-align: center;
        }
        
        .logout-button {
          background: linear-gradient(45deg, #ef4444, #dc2626);
          color: white;
          border: none;
          padding: 15px 40px;
          border-radius: 25px;
          font-size: 18px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          box-shadow: 0 8px 25px rgba(239, 68, 68, 0.3);
        }
        
        .logout-button:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 12px 35px rgba(239, 68, 68, 0.4);
        }
        
        .logout-button:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
        
        @media (max-width: 768px) {
          .profile-content {
            padding: 0 20px;
          }
          
          .info-content {
            margin-left: 0;
            margin-top: 15px;
          }
          
          .edit-field {
            flex-direction: column;
            align-items: stretch;
          }
          
          .edit-input {
            min-width: unset;
          }
          
          .stats-grid {
            flex-direction: column;
            gap: 20px;
          }
          
          .stat-divider {
            width: 40px;
            height: 1px;
          }
        }
      `}</style>
    </div>
  );
};

export default ProfilePage; 